import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatPct, formatSignedPct, formatUsd, type Rules, timeAgo, type VaultDetail } from "@v4ult/shared";
import { ArrowLeft, Loader2, Pause, Play, RefreshCw, Wallet } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AgentConsole, useAgentStream } from "../components/agent-console";
import { AllocationBars } from "../components/allocation-bars";
import { Card, mixColorMap, Page, PageHead, Pill, Seg } from "../components/app-ui";
import { LivePanel } from "../components/live-panel";
import { PerformanceChart } from "../components/performance-chart";
import { ChainBadge, DriftMeter } from "../components/vault-bits";
import { useWalletStep } from "../components/wallet";
import { runVaultNow, snapshotsQuery, updateVault, useAppConfig, vaultQuery } from "../lib/api";
import { cn } from "../lib/ui";

export const Route = createFileRoute("/app/vault/$id")({ component: VaultPage });

const RANGES = [
  { h: 24, l: "24H" },
  { h: 24 * 7, l: "7D" },
  { h: 24 * 30, l: "30D" },
];

function VaultPage() {
  const { id } = Route.useParams();
  const wallet = useWalletStep();
  const { data, error, isPending } = useQuery({ ...vaultQuery(id), enabled: wallet.signedIn });
  const vault = wallet.signedIn ? data : undefined;
  const stream = useAgentStream(vault?.id ?? null);

  if (!wallet.signedIn)
    return (
      <Page>
        <BackLink />
        <div className="mt-8">
          <PageHead
            title="Connect to see this vault"
            lead="Vaults are tied to the wallet that created them. Connect it and sign in to open this one."
            actions={
              <button
                type="button"
                onClick={wallet.run}
                disabled={wallet.pending}
                className="ap-btn ap-btn-pink ap-btn-md"
              >
                <Wallet size={18} aria-hidden="true" /> {wallet.label}
              </button>
            }
          />
        </div>
      </Page>
    );

  if (isPending)
    return (
      <Page>
        <BackLink />
        <p className="mt-10 flex items-center gap-3 text-[16px] text-(--ap-grey)" role="status">
          <Loader2 className="size-5 animate-spin text-(--ap-pink)" aria-hidden="true" /> Loading vault…
        </p>
      </Page>
    );
  if (error || !vault)
    return (
      <Page>
        <BackLink />
        <div className="mt-8">
          <PageHead
            title="This vault isn't here"
            lead={error?.message ?? "It may have been deleted, or it belongs to another wallet."}
            actions={
              <Link to="/app" className="ap-btn ap-btn-pink ap-btn-md">
                <ArrowLeft size={18} aria-hidden="true" /> Back to vaults
              </Link>
            }
          />
        </div>
      </Page>
    );
  return <VaultView key={vault.id} vault={vault} stream={stream} />;
}

function VaultView({ vault, stream }: { vault: VaultDetail; stream: ReturnType<typeof useAgentStream> }) {
  const qc = useQueryClient();
  const { data: config } = useAppConfig();
  const chain = config?.chains.find((c) => c.id === vault.chainId);
  const [hours, setHours] = useState(24);
  const { data: snaps } = useQuery(snapshotsQuery(vault.id, hours));
  const color = mixColorMap(vault.targets.map((t) => t.symbol));
  const assetOf = (s: string) => chain?.assets.find((a) => a.symbol === s);
  const refresh = () => qc.invalidateQueries({ queryKey: ["vault", vault.id] });

  const run = useMutation({
    mutationFn: () => runVaultNow(vault.id),
    onSuccess: (r) => {
      void refresh();
      toast(r.summary);
    },
    onError: (e) => toast.error(e.message),
  });
  const patch = useMutation({
    mutationFn: (input: Parameters<typeof updateVault>[1]) => updateVault(vault.id, input),
    onSuccess: (v) => {
      qc.setQueryData(["vault", vault.id], v);
      void qc.invalidateQueries({ queryKey: ["vaults"] });
    },
    onError: (e) => toast.error(e.message),
  });
  const working = stream.busy || run.isPending;

  const active = vault.status === "active";
  const pct = vault.change24hPct;
  return (
    <Page>
      <BackLink />
      <div className="mt-6">
        <PageHead
          eyebrow={
            <>
              <ChainBadge chainId={vault.chainId} />
              <Pill>
                <span
                  className={cn("ds-dot size-2", active ? "bg-[#18a957]" : "bg-black/25")}
                  data-pulse={active ? "on" : "off"}
                  aria-hidden="true"
                />
                {active ? "Agent active" : "Agent paused"}
              </Pill>
            </>
          }
          title={vault.name}
          actions={
            <>
              <button
                type="button"
                onClick={() => run.mutate()}
                disabled={working}
                className="ap-btn ap-btn-pink ap-btn-md"
              >
                <RefreshCw size={17} className={cn(working && "animate-spin")} aria-hidden />
                {working ? "Agent working…" : "Check now"}
              </button>
              <button
                type="button"
                onClick={() => patch.mutate({ status: active ? "paused" : "active" })}
                disabled={patch.isPending}
                className="ap-btn ap-btn-quiet h-[46px] px-5 text-[15.5px]"
              >
                {active ? <Pause size={17} aria-hidden /> : <Play size={17} aria-hidden />}
                {active ? "Pause agent" : "Resume agent"}
              </button>
            </>
          }
          aside={
            <div>
              <p className="ap-mono">Vault value</p>
              <p className="mt-2 text-[clamp(40px,4.6vw,60px)] leading-[1] font-[550] tracking-[-0.045em] tabular-nums">
                {formatUsd(vault.totalUsd)}
              </p>
              {pct !== null && (
                <p className="mt-2 font-mono text-[14px] text-(--ap-grey) tabular-nums">
                  {pct > 0 ? "▲" : pct < 0 ? "▼" : ""} {formatSignedPct(pct)} in 24h
                </p>
              )}
            </div>
          }
        />
      </div>

      <div className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Card
          id="card-value"
          label="Value"
          title="Value over time"
          aside={
            <Seg
              label="Chart range"
              options={RANGES.map((r) => ({ v: r.h, l: r.l }))}
              value={hours}
              onChange={setHours}
            />
          }
        >
          <PerformanceChart data={snaps ?? []} hours={hours} current={vault.totalUsd} />
        </Card>

        <Card id="card-drift" label="Drift" title="Holding the band">
          <div className="grid gap-5">
            <DriftMeter driftBps={vault.maxDriftBps} bandBps={vault.rules.driftThresholdBps} />
            <dl className="ap-panel grid grid-cols-2 gap-y-2.5 p-4 text-[14px]">
              <dt className="text-(--ap-grey)">Next check</dt>
              <dd className="text-right">{vault.nextCheckAt ? timeAgo(vault.nextCheckAt) : "Paused"}</dd>
              <dt className="text-(--ap-grey)">Last check</dt>
              <dd className="text-right">{timeAgo(vault.lastCheckedAt)}</dd>
              <dt className="text-(--ap-grey)">Last rebalance</dt>
              <dd className="text-right">{timeAgo(vault.lastRebalancedAt)}</dd>
            </dl>
            <RulesEditor
              rules={vault.rules}
              onSave={(rules) => patch.mutate({ rules })}
              saving={patch.isPending}
            />
          </div>
        </Card>

        <Card id="card-alloc" label="Allocation" title="Where it stands">
          <AllocationBars
            positions={vault.positions}
            bandBps={vault.rules.driftThresholdBps}
            color={color}
            assetOf={assetOf}
          />
        </Card>

        {chain && vault.address && (
          <Card id="card-controls" label="Controls" title="Your contract">
            <LivePanel
              key={vault.address}
              vault={vault}
              chain={chain}
              agentAddress={config?.agentAddress ?? null}
              onChange={refresh}
            />
          </Card>
        )}

        <Card
          id="card-agent"
          label="Agent"
          title="Agent activity"
          className="lg:col-span-2"
          aside={
            <p className="text-[14px] text-(--ap-grey) max-sm:hidden">
              Every decision, including the times it chose to hold
            </p>
          }
        >
          <AgentConsole vaultId={vault.id} chainId={vault.chainId} stream={stream} />
        </Card>
      </div>
    </Page>
  );
}

function BackLink() {
  return (
    <Link to="/app" className="ap-link">
      <ArrowLeft size={16} aria-hidden="true" /> Vaults
    </Link>
  );
}

const BANDS = [300, 500, 750, 1000];
const INTERVALS: { v: Rules["checkIntervalMin"]; l: string }[] = [
  { v: 15, l: "15m" },
  { v: 60, l: "1h" },
  { v: 240, l: "4h" },
  { v: 1440, l: "1d" },
];

function RulesEditor({
  rules,
  onSave,
  saving,
}: {
  rules: Rules;
  onSave: (r: Partial<Rules>) => void;
  saving: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <p className="text-[14px] text-(--ap-grey)">Rebalance band</p>
        <Seg
          className="flex w-full"
          label="Rebalance band"
          disabled={saving}
          options={BANDS.map((b) => ({ v: b, l: `±${b / 100}` }))}
          value={rules.driftThresholdBps}
          onChange={(v) => onSave({ driftThresholdBps: v })}
        />
      </div>
      <div className="grid gap-2">
        <p className="text-[14px] text-(--ap-grey)">Check every</p>
        <Seg
          className="flex w-full"
          label="Check every"
          disabled={saving}
          options={INTERVALS.map((i) => ({ v: i.v as number, l: i.l }))}
          value={rules.checkIntervalMin}
          onChange={(v) => onSave({ checkIntervalMin: v as Rules["checkIntervalMin"] })}
        />
      </div>
      <p className="text-[13px] text-(--ap-grey)">
        Slippage {formatPct(rules.maxSlippageBps)} · cost cap {formatPct(rules.maxCostBps)} · min swap{" "}
        {formatUsd(rules.minTradeUsd)}
      </p>
    </div>
  );
}
